/**
 * Composer post type: create a Space (rentable unit of business real
 * estate) and share it to the feed in one action.
 *
 * Architecture mirrors classType / jobType:
 *   1. initializeSpace(businessId) allocates a draft row
 *   2. updateSpace with status='active' populates + publishes
 *   3. Return a `space_share` payload; shell calls POST /posts
 *
 * Space posts are always business-authored — same as classes and jobs
 * — so `requiresBusiness: true` pins the composer to the selected
 * business and hides the "post as yourself" toggle.
 */

import {useEffect, useRef} from 'react'
import {initializeSpace, updateSpace, uploadSpaceMedia} from 'api'
import {spaceProfessions} from 'constants/onboarding'
import AddressAutocomplete from '../_shared/AddressAutocomplete'
import ProfessionPicker from '../_shared/ProfessionPicker'
import {
  CompactChips,
  Label,
  Section,
  Select,
  inputClass,
} from '../_shared/formKit'
import {addressFromBusiness} from '../_shared/businessAddress'
import closeIcon from 'assets/img/close.svg'

// Kept local to the plugin so the product copy for the composer is
// decoupled from the backend enum — if you rename a space_type value in
// the schema you still want control over the picker label here.
const spaceTypes = [
  {name: 'Chair', value: 'chair'},
  {name: 'Private Room', value: 'private_room'},
  {name: 'Shared Room', value: 'shared_room'},
  {name: 'Booth', value: 'booth'},
  {name: 'Studio Suite', value: 'studio_suite'},
  {name: 'Storefront', value: 'storefront'},
  {name: 'Other', value: 'other'},
]

const priceUnits = [
  {name: 'Hourly', value: 'hourly'},
  {name: 'Daily', value: 'daily'},
  {name: 'Weekly', value: 'weekly'},
  {name: 'Monthly', value: 'monthly'},
]

const MAX_BODY_LENGTH = 5000
const MAX_PHOTOS = 8
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024 // 10 MB

const createInitialState = ({business} = {}) => {
  // Default address inherits from the business — owners almost always
  // want a new space at their primary location. They can still pick a
  // different address per listing, but this saves the lookup.
  const seededAddress = addressFromBusiness(business)
  return {
    // Optional caption shown above the space card in the feed.
    body: '',
    title: '',
    description: '',
    spaceType: '',
    // Free text for how the rental is measured in practice (e.g. "no sqft,
    // single stylist chair"). Stored as a number when provided; empty
    // string on the form side represents "not applicable".
    squareFootage: '',
    price: '',
    priceUnit: 'monthly',
    contractTerms: '',
    // Free-text legacy display string. Always derived from address.formatted
    // when an address is selected; kept on the payload so the backend's
    // legacy `location` column has a fallback during cutover.
    location: seededAddress?.formatted || '',
    // Structured address from Google Places. See AddressAutocomplete for
    // the shape. Required for active publish; left null on drafts.
    address: seededAddress,
    // Eligible professions for this space — empty means "any". UAT
    // (2026/04/28): Amanda asked for this so renters can quickly filter
    // for setups appropriate to their work (hair vs. skin vs. nails).
    eligibleJobTypes: [],
    // Photos staged before the space is created. Each entry is
    // { file, previewUrl }. The preview URL is revoked on unmount.
    photos: [],
  }
}

const SpaceBody = ({state, setState, setError}) => {
  const titleRef = useRef(null)
  const photoInputRef = useRef(null)
  useEffect(() => {
    const t = setTimeout(() => titleRef.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [])

  // Revoke staged photo preview URLs on unmount.
  useEffect(() => {
    return () => {
      state.photos.forEach((p) => {
        if (p.previewUrl) URL.revokeObjectURL(p.previewUrl)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const update = (field) => (eventOrValue) => {
    const value =
      eventOrValue && eventOrValue.target
        ? eventOrValue.target.value
        : eventOrValue
    setState((prev) => ({...prev, [field]: value}))
    setError('')
  }

  const handlePhotosSelected = (files) => {
    const incoming = Array.from(files || [])
    if (incoming.length === 0) return

    const remaining = MAX_PHOTOS - state.photos.length
    if (remaining <= 0) {
      setError(`You can attach up to ${MAX_PHOTOS} photos per listing.`)
      return
    }

    const next = []
    for (const file of incoming.slice(0, remaining)) {
      if (!file.type?.startsWith('image/')) {
        setError('Photos must be images.')
        continue
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        setError('Each photo must be under 10 MB.')
        continue
      }
      next.push({file, previewUrl: URL.createObjectURL(file)})
    }
    if (next.length > 0) {
      setState((prev) => ({...prev, photos: [...prev.photos, ...next]}))
      setError('')
    }
  }

  const handleRemovePhoto = (index) => {
    setState((prev) => {
      const removed = prev.photos[index]
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl)
      return {...prev, photos: prev.photos.filter((_, i) => i !== index)}
    })
  }

  return (
    <div>
      <textarea
        value={state.body}
        onChange={update('body')}
        placeholder="Add an optional caption... (shown above the space card)"
        maxLength={MAX_BODY_LENGTH}
        rows={2}
        className="w-full resize-none border-0 outline-none text-sm text-sharpenn-black placeholder:text-green-gray bg-transparent focus:ring-0"
      />

      <Section step={1} title="Basics">
        <div>
          <Label>Listing title</Label>
          <input
            ref={titleRef}
            type="text"
            value={state.title}
            onChange={update('title')}
            placeholder="e.g. Private stylist chair, weekends only"
            className={inputClass}
          />
        </div>

        <div>
          <Label>Description</Label>
          <textarea
            value={state.description}
            onChange={update('description')}
            placeholder="Describe the space, amenities, what's included..."
            rows={3}
            className={`${inputClass} resize-none`}
          />
        </div>
      </Section>

      <Section step={2} title="Space details">
        <div>
          <Label>Space type</Label>
          <CompactChips
            options={spaceTypes}
            selected={state.spaceType ? [state.spaceType] : []}
            onChange={(next) => {
              setState((prev) => ({...prev, spaceType: next[0] || ''}))
              setError('')
            }}
          />
        </div>

        <div>
          <Label optional>Square footage</Label>
          <input
            type="number"
            min="0"
            step="1"
            value={state.squareFootage}
            onChange={update('squareFootage')}
            placeholder="N/A for chair rentals"
            className={inputClass}
          />
        </div>
      </Section>

      <Section step={3} title="Eligible professions">
        <div>
          <Label optional>Who can rent this space</Label>
          <ProfessionPicker
            options={spaceProfessions}
            value={state.eligibleJobTypes}
            placeholder="Select professions…"
            onChange={(next) => {
              setState((prev) => ({...prev, eligibleJobTypes: next}))
              setError('')
            }}
          />
        </div>
      </Section>

      <Section step={4} title="Pricing">
        <div>
          <Label>Rate</Label>
          <div className="flex gap-3">
            <input
              type="number"
              min="0"
              step="0.01"
              value={state.price}
              onChange={update('price')}
              placeholder="Price"
              className={`${inputClass} flex-1`}
            />
            <Select
              value={state.priceUnit}
              onChange={update('priceUnit')}
              className="w-36 flex-shrink-0"
            >
              {priceUnits.map((u) => (
                <option key={u.value} value={u.value}>
                  per {u.name.toLowerCase()}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div>
          <Label>Contract terms</Label>
          <textarea
            value={state.contractTerms}
            onChange={update('contractTerms')}
            placeholder="e.g. month-to-month, 2-month deposit, 30-day notice"
            rows={2}
            className={`${inputClass} resize-none`}
          />
        </div>
      </Section>

      <Section step={5} title="Location & photos">
        <div>
          <Label>Address</Label>
          <AddressAutocomplete
            value={state.address}
            onChange={(addr) => {
              setState((prev) => ({
                ...prev,
                address: addr,
                location: addr?.formatted || '',
              }))
              setError('')
            }}
            placeholder="Start typing the address…"
            className={inputClass}
          />
        </div>

        {/* Photos. Multi-image gallery — these are arguably the most
            important part of a rental listing. Uploaded after the space
            is published so they can attach to a real spacePostId. */}
        <div>
          <Label optional>Photos</Label>
          {state.photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-2">
              {state.photos.map((p, index) => (
                <div
                  key={p.previewUrl}
                  className="relative aspect-square rounded-lg overflow-hidden bg-black"
                >
                  <img
                    src={p.previewUrl}
                    alt={`Listing ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemovePhoto(index)}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center transition-colors"
                    aria-label="Remove photo"
                  >
                    <img
                      src={closeIcon}
                      alt=""
                      className="w-2.5 h-2.5 invert"
                    />
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            disabled={state.photos.length >= MAX_PHOTOS}
            className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-xl border border-dashed border-gray-200 bg-white text-sm text-sharpenn-slate hover:bg-sharpenn-cream transition-colors disabled:opacity-50"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            {state.photos.length === 0
              ? 'Add photos'
              : `Add more (${state.photos.length} / ${MAX_PHOTOS})`}
          </button>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => {
              handlePhotosSelected(e.target.files)
              e.target.value = ''
            }}
          />
        </div>
      </Section>
    </div>
  )
}

const canSubmit = (state) =>
  Boolean(
    state.title.trim() &&
    state.description.trim() &&
    state.spaceType &&
    state.address?.placeId &&
    state.price !== '' &&
    state.priceUnit &&
    state.contractTerms.trim()
  )

const submit = async ({state, authorCtx, setStatus}) => {
  if (!authorCtx.businessId) {
    throw new Error('Spaces must be posted as a business.')
  }

  setStatus('Creating space...')
  const initialized = await initializeSpace(authorCtx.businessId)
  const spacePostId = initialized?.id
  if (!spacePostId) {
    throw new Error('Could not initialize the space post. Please try again.')
  }

  await updateSpace({
    id: spacePostId,
    spaceData: {
      title: state.title.trim(),
      description: state.description.trim(),
      spaceType: state.spaceType,
      squareFootage:
        state.squareFootage === '' ? null : Number(state.squareFootage),
      price: Number(state.price),
      priceUnit: state.priceUnit,
      contractTerms: state.contractTerms.trim(),
      // Display-string fallback. The structured `address` object is the
      // authoritative source for the new columns; backend keeps `location`
      // as a backward-compat display string for one release.
      location: state.address?.formatted || state.location.trim(),
      address: state.address,
      eligibleJobTypes: state.eligibleJobTypes,
      businessId: authorCtx.businessId,
      status: 'active',
    },
  })

  // Upload photos one-by-one after the listing has a real id. Sequential
  // (not parallel) so the per-photo backend log lines stay readable and
  // we don't hit the upload-pipeline memory pressure that bites parallel
  // post_media uploads on small backend instances.
  if (state.photos.length > 0) {
    setStatus(`Uploading photo 1 of ${state.photos.length}...`)
    for (let i = 0; i < state.photos.length; i += 1) {
      setStatus(`Uploading photo ${i + 1} of ${state.photos.length}...`)
      const photo = state.photos[i]
      const formData = new FormData()
      formData.append('file', photo.file)
      formData.append('spacePostId', spacePostId)
      formData.append('mediaType', 'image')
      formData.append('type', 'post_media')
      if (authorCtx.businessId) {
        formData.append('businessId', authorCtx.businessId)
      }
      try {
        await uploadSpaceMedia({formData})
      } catch (err) {
        const backendMsg = err?.response?.data?.message
        throw new Error(
          backendMsg || `Space was created but photo ${i + 1} failed to upload.`
        )
      }
    }
  }

  setStatus('Posting...')
  // Shell handles POST /posts. createPost is idempotent for space_share
  // so composer retries won't stack duplicate share cards in the feed.
  return {
    type: 'space_share',
    businessId: authorCtx.businessId,
    spacePostId,
    body: state.body.trim() || null,
  }
}

const spacesType = {
  id: 'space',
  label: 'Space',
  icon: (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-5H10v5a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9z" />
    </svg>
  ),
  // Business-only, but offered to anyone with a business: the composer
  // asks which business to post as.
  canUse: ({actorKind, hasBusinesses}) =>
    actorKind === 'business' || Boolean(hasBusinesses),
  requiresBusiness: true,
  createInitialState,
  Body: SpaceBody,
  canSubmit,
  submit,
  submitLabel: 'Publish space',
}

export default spacesType
